import React, { useState, useEffect, useRef } from "react"
import { useParams, useHistory } from "react-router-dom"
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  getParceiroById,
  getTemplatesByParceiro,
  createParceiroTemplate,
  updateParceiroTemplate,
  deleteParceiroTemplate,
  setParceiroTemplateAtivo,
} from "helpers/parceiro_helper"
import { FaEdit, FaTrash, FaPlus, FaStar, FaRegStar, FaArrowLeft, FaEye } from "react-icons/fa"
import PreviewPDF from "./PreviewPDF"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const templateVazio = {
  Nome: "",
  CorPrimaria: "#000000",
  CorSecundaria: "#ffffff",
}

const ParceiroTemplates = () => {
  const { id } = useParams()
  const history = useHistory()

  const [parceiro, setParceiro] = useState(null)
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)

  const [modalAberto, setModalAberto] = useState(false)
  const [templateEditando, setTemplateEditando] = useState(null)
  const [form, setForm] = useState(templateVazio)
  const [logoFile, setLogoFile] = useState(null)
  const [salvando, setSalvando] = useState(false)
  const logoInputRef = useRef(null)

  const [templatePreview, setTemplatePreview] = useState(null)

  document.title = parceiro ? `Templates — ${parceiro.Nome} | Blops Leads` : "Templates | Blops Leads"

  useEffect(() => {
    carregarDados()
  }, [id])

  const carregarDados = async () => {
    setLoading(true)
    try {
      const [p, tmpl] = await Promise.all([
        getParceiroById(id),
        getTemplatesByParceiro(id),
      ])
      setParceiro(p)
      setTemplates(tmpl)
    } catch {
      toastr.error("Erro ao carregar dados", "Erro")
    } finally {
      setLoading(false)
    }
  }

  const carregarTemplates = async () => {
    try {
      const data = await getTemplatesByParceiro(id)
      setTemplates(data)
    } catch {
      toastr.error("Erro ao carregar templates", "Erro")
    }
  }

  const abrirModalNovo = () => {
    setTemplateEditando(null)
    setForm(templateVazio)
    setLogoFile(null)
    if (logoInputRef.current) logoInputRef.current.value = ""
    setModalAberto(true)
  }

  const abrirModalEditar = t => {
    setTemplateEditando(t)
    setForm({
      Nome: t.Nome,
      CorPrimaria: t.CorPrimaria || "#000000",
      CorSecundaria: t.CorSecundaria || "#ffffff",
    })
    setLogoFile(null)
    if (logoInputRef.current) logoInputRef.current.value = ""
    setModalAberto(true)
  }

  const fecharModal = () => {
    setModalAberto(false)
    setTemplateEditando(null)
    setForm(templateVazio)
    setLogoFile(null)
  }

  const handleSalvar = async () => {
    if (!form.Nome.trim()) {
      toastr.warning("Por favor, informe o nome do template", "Atenção")
      return
    }
    const fd = new FormData()
    fd.append("Nome", form.Nome)
    fd.append("CorPrimaria", form.CorPrimaria)
    fd.append("CorSecundaria", form.CorSecundaria)
    if (logoFile) fd.append("logo", logoFile)

    setSalvando(true)
    try {
      if (templateEditando) {
        await updateParceiroTemplate(id, templateEditando.Id, fd)
        toastr.success("Template atualizado com sucesso!", "Sucesso")
      } else {
        await createParceiroTemplate(id, fd)
        toastr.success("Template criado com sucesso!", "Sucesso")
      }
      fecharModal()
      carregarTemplates()
    } catch (error) {
      toastr.error(error.response?.data?.message || "Erro ao salvar template", "Erro")
    } finally {
      setSalvando(false)
    }
  }

  const handleAtivar = async templateId => {
    try {
      await setParceiroTemplateAtivo(id, templateId)
      toastr.success("Template ativado com sucesso!", "Sucesso")
      carregarTemplates()
    } catch (error) {
      toastr.error(error.response?.data?.message || "Erro ao ativar template", "Erro")
    }
  }

  const handleExcluir = async templateId => {
    if (window.confirm("Deseja realmente excluir este template?")) {
      try {
        await deleteParceiroTemplate(id, templateId)
        toastr.success("Template excluído com sucesso!", "Sucesso")
        carregarTemplates()
      } catch (error) {
        toastr.error(error.response?.data?.message || "Erro ao excluir template", "Erro")
      }
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title="Parceiros"
            breadcrumbItem={parceiro ? `Templates — ${parceiro.Nome}` : "Templates"}
          />

          <Row className="mb-3">
            <Col>
              <Button color="secondary" size="sm" onClick={() => history.push("/parceiros")}>
                <FaArrowLeft className="me-1" /> Voltar
              </Button>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">
                      {parceiro ? `Templates de ${parceiro.Nome}` : "Templates"}
                    </h4>
                    <Button color="primary" onClick={abrirModalNovo}>
                      <FaPlus className="me-1" /> Novo Template
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Carregando...</span>
                      </div>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>Nenhum template cadastrado para este parceiro.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Nome</th>
                            <th style={{ width: "130px" }}>Cor Primária</th>
                            <th style={{ width: "140px" }}>Cor Secundária</th>
                            <th style={{ width: "80px" }}>Logo</th>
                            <th style={{ width: "80px" }} className="text-center">Status</th>
                            <th style={{ width: "160px" }} className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map(t => (
                            <tr key={t.Id}>
                              <td>{t.Nome}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 4,
                                      backgroundColor: t.CorPrimaria || "#ccc",
                                      border: "1px solid #dee2e6",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <small className="text-muted">{t.CorPrimaria || "—"}</small>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 4,
                                      backgroundColor: t.CorSecundaria || "#ccc",
                                      border: "1px solid #dee2e6",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <small className="text-muted">{t.CorSecundaria || "—"}</small>
                                </div>
                              </td>
                              <td>
                                {t.LogoPath ? (
                                  <img
                                    src={t.LogoPath}
                                    alt="logo"
                                    style={{ height: 28, objectFit: "contain" }}
                                  />
                                ) : (
                                  <small className="text-muted">—</small>
                                )}
                              </td>
                              <td className="text-center">
                                {t.Ativo ? (
                                  <Badge color="success">Ativo</Badge>
                                ) : (
                                  <Badge color="secondary">Inativo</Badge>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-1"
                                  title="Visualizar"
                                  onClick={() => setTemplatePreview(t)}
                                >
                                  <FaEye />
                                </Button>
                                <Button
                                  color="warning"
                                  size="sm"
                                  className="me-1"
                                  title={t.Ativo ? "Template ativo" : "Ativar"}
                                  onClick={() => !t.Ativo && handleAtivar(t.Id)}
                                  disabled={t.Ativo}
                                >
                                  {t.Ativo ? <FaStar /> : <FaRegStar />}
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-1"
                                  title="Editar"
                                  onClick={() => abrirModalEditar(t)}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  title={t.Ativo && templates.length > 1 ? "Ative outro template antes de excluir" : "Excluir"}
                                  onClick={() => handleExcluir(t.Id)}
                                  disabled={t.Ativo && templates.length > 1}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={!!templatePreview} toggle={() => setTemplatePreview(null)} size="lg" centered>
        <ModalHeader toggle={() => setTemplatePreview(null)}>
          Preview — {templatePreview?.Nome}
        </ModalHeader>
        <ModalBody style={{ display: "flex", justifyContent: "center", backgroundColor: "#e9ecef", padding: "24px 16px" }}>
          {templatePreview && <PreviewPDF template={templatePreview} />}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setTemplatePreview(null)}>Fechar</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalAberto} toggle={fecharModal} size="md">
        <ModalHeader toggle={fecharModal}>
          {templateEditando ? "Editar Template" : "Novo Template"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Nome *</label>
            <Input
              type="text"
              placeholder="Nome do template"
              value={form.Nome}
              onChange={e => setForm(f => ({ ...f, Nome: e.target.value }))}
              disabled={salvando}
            />
          </div>
          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Cor Primária</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.CorPrimaria}
                  onChange={e => setForm(f => ({ ...f, CorPrimaria: e.target.value }))}
                  disabled={salvando}
                  style={{ width: 48, padding: 2 }}
                />
                <Input
                  type="text"
                  value={form.CorPrimaria}
                  onChange={e => setForm(f => ({ ...f, CorPrimaria: e.target.value }))}
                  disabled={salvando}
                  style={{ fontFamily: "monospace" }}
                />
              </div>
            </Col>
            <Col md={6}>
              <label className="form-label">Cor Secundária</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.CorSecundaria}
                  onChange={e => setForm(f => ({ ...f, CorSecundaria: e.target.value }))}
                  disabled={salvando}
                  style={{ width: 48, padding: 2 }}
                />
                <Input
                  type="text"
                  value={form.CorSecundaria}
                  onChange={e => setForm(f => ({ ...f, CorSecundaria: e.target.value }))}
                  disabled={salvando}
                  style={{ fontFamily: "monospace" }}
                />
              </div>
            </Col>
          </Row>
          <div className="mb-3">
            <label className="form-label">
              Logo{templateEditando && " (deixe vazio para manter o atual)"}
            </label>
            {templateEditando && templateEditando.LogoPath && (
              <div className="mb-2">
                <img
                  src={templateEditando.LogoPath}
                  alt="logo atual"
                  style={{ height: 40, objectFit: "contain" }}
                />
              </div>
            )}
            <Input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              innerRef={logoInputRef}
              onChange={e => setLogoFile(e.target.files[0] || null)}
              disabled={salvando}
            />
            <small className="text-muted">PNG, JPG ou SVG — máx. 5 MB</small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={fecharModal} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSalvar} disabled={salvando}>
            {salvando ? "Salvando..." : templateEditando ? "Atualizar" : "Criar"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default ParceiroTemplates
